import React, {
  ReactNode, FC, useMemo, useState, useCallback, useEffect, useRef, KeyboardEvent, ReactElement,
  useContext,
} from 'react';
import { PopperOptions, TriggerType, usePopperTooltip } from 'react-popper-tooltip';
import noop from 'lodash/noop';
import { ThemeContext } from 'styled-components';
import { Placement } from '@popperjs/core';
import { TooltipOffset } from 'ia-react-core/~/interfaces/TooltipOffset';
import { SCP } from 'ia-react-core/~/interfaces/SCP';
import KEY_CODES from '../../../constants/KEY_CODES';
import useCreateOptimizedPopper from '../../../hooks/_useCreateOptimizedPopper';
import { StyledTooltip, StyledArrow, StyledTooltipWrapper } from './Tooltip.styles';

export interface TooltipRenderProps {
  isVisible: boolean;
  toggleTooltip: ((nextVisible?: boolean) => void);
}
export interface TooltipProps extends SCP<any> {
  /** Tip message. */
  tip: ReactNode | ((options: TooltipRenderProps) => ReactNode);
  /** The element to attach the tooltip on. */
  children: ReactNode | ((options: TooltipRenderProps) => ReactNode);
  /** Determine when to show/hide the tooltip. */
  trigger?: TriggerType | TriggerType[];
  /** Offset from the element the tooltip is attached on. */
  offset?: TooltipOffset;
  /** Sets the padding of the tooltip. */
  tooltipPadding?: number;
  /** Delay before the the popper hides. */
  delayHide?: number;
  /** Delay before the the popper shows. */
  delayShow?: number;
  /** Css position applied to the StyledTooltipWrapper. */
  position?: string;
  /** Sets the background color of the tooltip */
  backgroundColor?: string;
  /** Sets the font color */
  color?: string;
  /** PopperJs placement option for the tooltip. */
  placement?: Placement;
  /** Callback triggered when the tooltip closes. */
  onClose?: () => void;
}

interface TooltipInternalProps {
  tooltipId: number;
}

interface TooltipComposedProps extends TooltipProps, TooltipInternalProps {}

const TooltipDefaultProps = {
  trigger: 'click',
  placement: 'top',
  tooltipPadding: 16,
  position: 'absolute',
  backgroundColor: 'white',
  color: 'black',
} as const;

const Tooltip: FC<TooltipComposedProps> = ({
  children, tip, offset, placement, trigger, tooltipId, tooltipPadding, position, backgroundColor, color, delayHide, delayShow, onClose, ...props
}) => {
  const [controlledVisible, setControlledVisible] = useState(false);
  const visibleRef = useRef(false);
  const [positioned, setPositioned] = useState(false);
  const tooltipOptions = useMemo(() => ({
    trigger,
    delayHide,
    delayShow,
  }), [delayHide, delayShow, trigger]);

  const createPopper = useCreateOptimizedPopper(controlledVisible);
  const theme = useContext(ThemeContext);
  useEffect(() => {
    if (controlledVisible) {
      const timer = setTimeout(() => {
        setPositioned(true);
      }, 10);
      return () => {
        clearTimeout(timer);
      };
    }

    setPositioned(false);

    return noop;
  }, [controlledVisible]);

  const popperOptions = useMemo<PopperOptions>(() => ({
    createPopper,
    placement,
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          padding: 16,
        },
      },
      {
        name: 'offset',
        options: {
          offset,
        },
      },
    ],
  }), [createPopper, offset, placement]);

  const {
    getArrowProps,
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    visible,
  } = usePopperTooltip(tooltipOptions, popperOptions);

  const triggerRef = useRef<HTMLDivElement>();
  const mergeTriggerRefs = useCallback((triggerElement: HTMLDivElement) => {
    triggerRef.current = triggerElement;
    setTriggerRef(triggerElement);
  }, [setTriggerRef]);

  useEffect(() => {
    if (controlledVisible && !visible && onClose) {
      onClose();
    }

    setControlledVisible(visible);
    visibleRef.current = visible;
  }, [controlledVisible, onClose, visible]);

  const toggleTooltip = useCallback((nextVisible = !visibleRef.current) => {
    if (nextVisible !== visibleRef.current) {
      triggerRef.current?.click();
    }
  }, []);

  const renderProps = useMemo(() => ({
    toggleTooltip,
    isVisible: controlledVisible,
  }), [toggleTooltip, controlledVisible]);

  const childrenArray = useMemo(() => {
    if (typeof children === 'function') {
      return React.Children.toArray(children(renderProps));
    }

    return React.Children.toArray(children);
  }, [children, renderProps]);

  const firstChild = useMemo(() => childrenArray[0], [childrenArray]) as ReactElement;

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.keyCode === KEY_CODES.ESC && visible) {
      triggerRef.current?.click();
    }

    if ('onKeyDown' in firstChild.props) {
      firstChild.props.onKeyDown?.(event);
    }
  }, [firstChild?.props, visible]);

  const describedby = `Tooltip${tooltipId}`;

  const targetClone = useMemo(() => {
    if (!firstChild) {
      return children;
    }

    return React.cloneElement(firstChild, {
      ref: mergeTriggerRefs,
      'aria-describedby': describedby,
      isVisible: controlledVisible,
      onKeyDown: handleKeyDown,
    });
  }, [firstChild, mergeTriggerRefs, describedby, controlledVisible, handleKeyDown, children]);

  const tooltipProps = useMemo(() => getTooltipProps(), [getTooltipProps]);
  const arrowProps = useMemo(() => getArrowProps(), [getArrowProps]);

  return (
    <>
      {targetClone}
      <StyledTooltipWrapper position={position}>
        <StyledTooltip
          role="tooltip"
          ref={setTooltipRef}
          visible={controlledVisible}
          aria-hidden="false"
          id={describedby}
          positioned={positioned}
          tooltipPadding={tooltipPadding}
          backgroundColor={backgroundColor}
          color={color}
          {...tooltipProps}
          style={{
            ...tooltipProps?.style,
            position: theme?.Tooltip?.position ? theme.Tooltip?.position : tooltipProps?.style?.position,
            width: theme?.Tooltip?.position ? 'max-content' : tooltipProps?.style?.width,
          }}
          {...props}
        >
          <StyledArrow {...arrowProps} />
          {typeof tip === 'function' ? tip({ toggleTooltip, isVisible: controlledVisible }) : tip}
        </StyledTooltip>
      </StyledTooltipWrapper>
    </>
  );
};

Tooltip.defaultProps = TooltipDefaultProps;

export default Tooltip;
